import React, { useEffect, useState } from "react";
import { trackEvent } from "../../../utils/analytics";
import { getWeddingDate } from "../../../utils/formatDate";

import ShareUi from "./ShareUI";
import { INVITATION_OCCASION, Invitations } from "../../../store/services/invitations/dto/invitations.dto";
import ShareWeddingMemoriesByEmail from "./ShareWeddingMemoriesByEmail";
import ShareByQRModal from "./ShareByQRModal";
import { handleCopyToClipboard } from "../../../utils/copyToClipboard";
import { useUpdateInvitationHTMLMutation } from "../../../store/services/invitations/invitations.service";

const getOccasionType = (data: Invitations, isEng: boolean) => {
	switch (data?.occasion) {
		case INVITATION_OCCASION.ENGAGEMENT: {
			return isEng ? "Engagement" : "خِطبه";
		}
		case INVITATION_OCCASION.KATB_KTAB: {
			return isEng ? "Akd Karaan" : "عقد قران";
		}
		default: {
			return isEng ? "Wedding" : "زِفاف";
		}
	}
};

const ShareWeddingMemoriesModal = ({
	open,
	onClose,
	data
}: {
	data: Invitations;
	open: boolean;
	onClose: () => void;
}) => {
	const [shareByEmail, setShareByEmail] = useState(false);
	const [shareByQR, setShareByQR] = useState(false);
	const [callUpdateHTML, { isLoading }] = useUpdateInvitationHTMLMutation();
	const updatedTimeStamp = new Date(data?.updatedAt || "").getTime();
	const shareUrl = `${process.env.REACT_APP_SHARE || "http://localhost:3000"}/weddingInvitation/${data._id}/memories-${updatedTimeStamp}.html`;

	useEffect(() => {
		callUpdateHTML({ _id: data._id, memories: true });
	}, [data._id]);

	const handleShareToWhatsApp = () => {
		trackEvent("share_whatsapp_invitation_memories", {
			scope: "Invitations_memories",
			description: "Share WhatsApp Invitation Memories"
		});
		const occasionType = getOccasionType(data, data.language === "EN");
		const message =
			data.language === "EN"
				? `Our special day, through your eyes - ${occasionType} — ${getWeddingDate(data.wedding_date, data.language, data.timezone)} - album : ${shareUrl}`
				: ` لحظات يومنا البهـيج بعيونكم - ${occasionType}  - الالبوم : ${getWeddingDate(data.wedding_date, data.language, data.timezone)} ${shareUrl}`;

		const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
		window.open(whatsappUrl, "_blank");
	};

	const handleFacebookShare = () => {
		trackEvent("share_facebook_invitation_memories", {
			scope: "Invitations_memories",
			description: "Share Facebook Invitation Memories"
		});
		const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
		window.open(facebookShareUrl, "_blank");
	};

	const handleFaceBookMessengerShare = () => {
		const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
		trackEvent("share_fb_messenger_invitation_memories", {
			scope: "Invitations_memories",
			description: "Share FB Messenger Invitation Memories"
		});
		const messengerShareUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=${appId}&redirect_uri=${encodeURIComponent(shareUrl)}`;
		window.open(messengerShareUrl, "_blank");
	};

	const handleTwitterShare = () => {
		const occasionType = getOccasionType(data, data.language === "EN");
		const tweetText =
			data.language === "EN"
				? `Our special day, through your eyes — ${occasionType} - ${getWeddingDate(data.wedding_date, data.language, data.timezone)}`
				: `لحظات يومنا البهـيج بعيونكم - ${occasionType} - ${getWeddingDate(data.wedding_date, data.language, data.timezone)}`;
		const hashtags = "Wedding,event,WeddingInvitation,OurBigDayAlbum,WeddingAlbum,Memories";
		trackEvent("share_X_invitation_memeories", {
			scope: "Invitations_memories",
			description: "Share X Invitation Memories"
		});
		const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`;
		window.open(twitterUrl, "_blank");
	};

	const handleDeviceShare = async () => {
		try {
			if (navigator.share) {
				await navigator.share({
					title: "Wedding Invitation",
					url: shareUrl
				});
			}
		} catch (err) {
			console.info("Share failed:", err);
		}
	};

	return (
		<>
			<ShareUi
				open={open}
				onClose={onClose}
				handleTwitterShare={handleTwitterShare}
				handleShareToWhatsApp={handleShareToWhatsApp}
				handleCopyToClipboard={() => handleCopyToClipboard(shareUrl)}
				handleFacebookShare={handleFacebookShare}
				handleFaceBookMessengerShare={handleFaceBookMessengerShare}
				shareUrl={shareUrl}
				enableEmail={true}
				handleEmailShare={() => setShareByEmail(true)}
				enableQRCode={true}
				openQRCode={() => setShareByQR(true)}
				handleDeviceShare={handleDeviceShare}
				loading={isLoading}
			/>
			{shareByEmail && data?._id && (
				<ShareWeddingMemoriesByEmail open={shareByEmail} id={data._id} onClose={() => setShareByEmail(false)} />
			)}
			{shareByQR && (
				<ShareByQRModal
					id={data._id}
					open={shareByQR}
					qr_url={data?.memories_qr_code}
					onClose={() => setShareByQR(false)}
				/>
			)}
		</>
	);
};

export default ShareWeddingMemoriesModal;
